/**
 * Deep-link paths for Pro mobile notification list + push tap handling.
 *
 * Destination policy:
 * - Prefer a real Pro-shell or public SPA route that can show the content.
 * - Notes have no Pro mobile surface — return null (no workspace dead-end,
 *   no false chevron). Push tap falls back to /pro/notifications when no link.
 * - Outreach: respond-token and public report URLs are real destinations;
 *   sharer "recipient responded" needs desktop analytics — unsupported here.
 * - Cold push taps receive the same metadata shape as the in-app list when the
 *   server includes it on the FCM data payload.
 */

export interface ProNotificationLinkInput {
  notification_type: string;
  entity_id?: string | null;
  workspace_id?: string | null;
  metadata?: Record<string, unknown> | null;
}

/** Types that deliberately have no Pro destination until a surface ships. */
export const PRO_NOTIFICATION_UNSUPPORTED_DESTINATION_TYPES = new Set([
  'note_created',
  'note_shared',
  'outreach_recipient_responded',
]);

export type ProNotificationDestinationKind = 'link' | 'unsupported' | 'none';

export interface ProNotificationDestination {
  kind: ProNotificationDestinationKind;
  /** In-app path when kind === 'link'; null otherwise. */
  href: string | null;
  /** Short label when kind === 'unsupported' (list UI). */
  label?: string;
}

function metadataString(
  metadata: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  if (!metadata) return null;
  const value = metadata[key];
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

/**
 * Absolute public URLs from notification metadata → same-origin path for
 * in-app navigation (Capacitor SPA). External origins are ignored so we
 * never claim an in-app destination we cannot open.
 */
export function pathFromSameOriginUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    if (url.startsWith('/')) return url;
    const parsed = new URL(url);
    if (typeof window !== 'undefined' && window.location?.origin) {
      if (parsed.origin !== window.location.origin) return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}` || null;
  } catch {
    return null;
  }
}

export function getProNotificationDestination(
  n: ProNotificationLinkInput,
): ProNotificationDestination {
  switch (n.notification_type) {
    case 'bill_added_to_workspace':
      if (n.entity_id) return { kind: 'link', href: `/pro/bills/${n.entity_id}` };
      return {
        kind: 'link',
        href: n.workspace_id ? `/pro/workspaces/${n.workspace_id}` : '/pro/bills',
      };
    case 'calendar_event_created':
      return { kind: 'link', href: '/pro/calendar' };
    case 'note_created':
    case 'note_shared':
      // No Notes surface on Pro mobile — do not deep-link to workspace detail
      // (dead end) or claim note content can be shown.
      return {
        kind: 'unsupported',
        href: null,
        label: 'Notes are not available in this app yet',
      };
    case 'workspace_invitation':
      return { kind: 'link', href: '/pro/notifications' };
    case 'system_announcement':
      return { kind: 'none', href: null };
    case 'outreach_response_requested': {
      const token = metadataString(n.metadata, 'token');
      if (token) return { kind: 'link', href: `/respond/${token}` };
      // Token missing or invalid — honest unsupported (no broken respond URL).
      return {
        kind: 'unsupported',
        href: null,
        label: 'Response link unavailable',
      };
    }
    case 'outreach_recipient_responded':
      // Desktop outreach analytics only; no Pro mobile outreach surface.
      return {
        kind: 'unsupported',
        href: null,
        label: 'Review outreach responses on the web app',
      };
    case 'outreach_update': {
      const reportPath = pathFromSameOriginUrl(metadataString(n.metadata, 'report_url'));
      if (reportPath) return { kind: 'link', href: reportPath };
      return {
        kind: 'unsupported',
        href: null,
        label: 'Outreach updates open from the web app when no public report link is available',
      };
    }
    default:
      return { kind: 'none', href: null };
  }
}


/**
 * Map an FCM / Capacitor push `data` bag onto the same input the in-app list uses.
 * Metadata arrives as a JSON string (FCM data values are strings only).
 */
export function notificationLinkInputFromPushData(
  data: Record<string, unknown> | null | undefined,
): ProNotificationLinkInput {
  const raw = data || {};
  return {
    notification_type: String(raw.notificationType || ''),
    entity_id: raw.entityId ? String(raw.entityId) : null,
    workspace_id: raw.workspaceId ? String(raw.workspaceId) : null,
    metadata: parsePushMetadata(raw.metadata),
  };
}

function parsePushMetadata(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Invalid JSON — treat as missing so the resolver stays on the unsupported path.
  }
  return null;
}

/** Path for navigation / push resolveLink; null means use fallback or no-op. */
export function getProNotificationLink(n: ProNotificationLinkInput): string | null {
  return getProNotificationDestination(n).href;
}
